/**
 * WorkflowStageContainer.tsx — Stage / item 容器行（CC Agent 同构）
 */
import { useCallback, useEffect, useMemo, useState, memo, type CSSProperties } from 'react';
import { useStore } from 'zustand';

import {
  buildStageDoneSummary,
  formatTeaserExpandHint,
} from '../../core/workflow/stageDoneSummary';
import { useWorkflowLoopReplayHandler } from '../../core/context/WorkflowLoopReplayContext';
import { useWorkflowSessionStoreApi } from '../../core/context/WorkflowSessionContext';
import { useSessionViewOptions } from '../../core/context/SessionContext';
import {
  createWorkflowRunningLoopTree,
  isWorkflowLoopTreeActive,
} from '../../core/workflow/workflowLoopTreeUtils';
import type { SessionTree } from '../../types/tree';
import type { WorkflowContainerRuntimeNode } from '../../types/workflowContainer';
import { AgentLoopView } from '../agentLoop/AgentLoopView';
import { WorkflowContainerLine } from './WorkflowContainerLine';
import { WorkflowStageDoneBody } from './workflowStageDoneBody';
import { isActiveWorkflowContainer } from './workflowStageListUtils';
import {
  buildStageLineMeta,
  mapContainerStatusLabel,
  mapContainerUiStatus,
  workflowIndentUnit,
} from './workflowContainerLineUtils';
import {
  workflowContainerNodeVisualEqual,
  workflowContainerViewPropsEqual,
} from './workflowContainerMemo';

export interface WorkflowStageContainerProps {
  node: WorkflowContainerRuntimeNode;
  loopTree?: SessionTree;
  depth?: number;
  virtualized?: boolean;
  virtualizeThreshold?: number;
  groupParallelTools?: boolean;
  /** 嵌套 parallel 宿主 stage：仅作壳，不展示 scoped loop / Spinner */
  suppressHostScopedLoop?: boolean;
  /** 子树内仍有 active 容器时，宿主 stage 不得显示 done（stage-done 可能早于 agent 结束） */
  subtreeHasActiveWork?: boolean;
}

function containerTestId(node: WorkflowContainerRuntimeNode): string {
  if (node.scope === 'item') return `lax-workflow-parallel-${node.scope_key}`;
  if (node.scope === 'stage') return `lax-workflow-stage-${node.scope_key}`;
  return `lax-workflow-container-${node.scope_key}`;
}

function bodyTestId(node: WorkflowContainerRuntimeNode): string {
  if (node.scope === 'item') return `lax-workflow-parallel-body-${node.scope_key}`;
  return `lax-workflow-stage-body-${node.scope_key}`;
}

export function WorkflowStageContainerInner({
  node,
  loopTree,
  depth = 0,
  virtualized,
  virtualizeThreshold,
  groupParallelTools,
  suppressHostScopedLoop = false,
  subtreeHasActiveWork = false,
}: WorkflowStageContainerProps) {
  const { verbose } = useSessionViewOptions();
  const store = useWorkflowSessionStoreApi();
  const replayHandler = useWorkflowLoopReplayHandler();
  const activeLoopSessionId = useStore(store, (s) => s.state.activeLoopSessionId);
  const activeContainerIds = useStore(store, (s) => s.state.containerTree.activeContainerIds);
  const cachedSummary = useStore(
    store,
    (s) => s.state.stageDoneSummariesByContainerId[node.container_id],
  );
  const pinLoopSession = useStore(store, (s) => s.pinLoopSession);

  const isRunning = node.status === 'running' || node.status === 'retrying';
  const isDone = node.status === 'completed' || node.status === 'failed';
  const isSkipped = node.status === 'skipped';
  const loopStillActive = isWorkflowLoopTreeActive(loopTree);
  const effectivelyRunning =
    isRunning || loopStillActive || (subtreeHasActiveWork && isDone);
  const effectivelyDone = isDone && !subtreeHasActiveWork && !loopStillActive;

  const uiStatus = mapContainerUiStatus(effectivelyRunning ? 'running' : node.status);

  const isActive = isActiveWorkflowContainer(node, activeLoopSessionId, activeContainerIds);

  const doneSummaryFromTree = useMemo(
    () => (loopTree && isDone ? buildStageDoneSummary(loopTree) : null),
    [loopTree, isDone],
  );
  const doneSummary = doneSummaryFromTree ?? (isDone ? cachedSummary : null);

  /** 原则 A：仍在执行 → 保持展开；原则 B：effectivelyDone → 立刻折（不等兄弟） */
  const shouldAutoExpandRunning = effectivelyRunning && !isSkipped;

  const [expanded, setExpanded] = useState(shouldAutoExpandRunning);

  useEffect(() => {
    if (shouldAutoExpandRunning) {
      setExpanded(true);
      return;
    }
    if (effectivelyDone || isSkipped) {
      setExpanded(false);
    }
  }, [shouldAutoExpandRunning, effectivelyDone, isSkipped]);

  const requestLoopHydration = useCallback(() => {
    const loopSessionId = node.loopSessionId;
    if (!loopSessionId || loopTree) return;
    pinLoopSession(loopSessionId);
    void replayHandler?.(loopSessionId);
  }, [node.loopSessionId, loopTree, pinLoopSession, replayHandler]);

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => {
      const next = !prev;
      if (next && isDone && node.loopSessionId && !loopTree) {
        requestLoopHydration();
      }
      return next;
    });
  }, [isDone, node.loopSessionId, loopTree, requestLoopHydration]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'o') return;
      setExpanded(true);
      requestLoopHydration();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [requestLoopHydration]);

  const testId = containerTestId(node);
  const isItem = node.scope === 'item';
  const displayTitle = node.title;

  const usesScopedLoop = Boolean(node.loopSessionId) && !suppressHostScopedLoop;
  const runningLoopTree: SessionTree | undefined =
    loopTree
    ?? (usesScopedLoop && effectivelyRunning ? createWorkflowRunningLoopTree() : undefined);
  const itemLoopVirtualized = isItem ? false : virtualized;
  const staticContentTeaser =
    !usesScopedLoop && !loopTree && node.content_blocks.length > 0
      ? node.content_blocks[node.content_blocks.length - 1]!.preview
      : '';
  const teaserText = doneSummary?.teaser || staticContentTeaser;
  const expandHint =
    doneSummary ? formatTeaserExpandHint(doneSummary.extraLines, verbose) : null;
  const doneLabel = doneSummary?.label ?? 'done';
  const statusLabel = effectivelyDone
    ? doneLabel
    : suppressHostScopedLoop && effectivelyRunning
      ? 'running'
      : mapContainerStatusLabel(effectivelyRunning ? 'running' : node.status);

  const canToggleShell = (effectivelyRunning && !isSkipped) || effectivelyDone;
  const shellToggle = canToggleShell ? toggleExpanded : undefined;

  return (
    <div
      className={`lax-workflow-stage-container lax-workflow-stage-container--${uiStatus}${
        isSkipped ? ' lax-workflow-stage-container--skipped' : ''
      }${isItem ? ' lax-workflow-parallel-item' : ' lax-workflow-stage'}`}
      data-testid={testId}
      data-stage-status={uiStatus}
      data-container-scope={node.scope}
      data-active={isActive ? 'true' : undefined}
      style={
        {
          '--lax-workflow-depth': depth,
          '--lax-workflow-indent-unit': workflowIndentUnit(depth),
        } as CSSProperties
      }
    >
      <WorkflowContainerLine
        title={displayTitle}
        description={node.subtitle || undefined}
        uiStatus={uiStatus}
        meta={buildStageLineMeta(node)}
        statusLabel={statusLabel}
        onToggle={shellToggle}
        interactive={canToggleShell}
        expanded={expanded}
        headerTestId={effectivelyDone ? `${testId}-done` : undefined}
        titleTestId={`${testId}-title`}
        descriptionTestId={`${testId}-desc`}
        className={
          isItem
            ? 'lax-workflow-parallel-item__header'
            : undefined
        }
      />

      {effectivelyDone && !expanded && (teaserText || expandHint) ? (
        <div className="lax-workflow-stage-container__done-teaser">
          {teaserText ? (
            <span
              className="lax-workflow-stage-container__done-teaser-line"
              data-testid={`${testId}-teaser`}
            >
              {`⎿ ${teaserText}`}
            </span>
          ) : null}
          {expandHint ? (
            <span className="lax-workflow-expand-trigger__hint">{expandHint}</span>
          ) : null}
        </div>
      ) : null}

      {expanded && isRunning && !usesScopedLoop && node.content_blocks.length > 0 ? (
        <div
          className="lax-workflow-stage-container__body lax-workflow-stage-container__content-preview"
          data-testid={bodyTestId(node)}
        >
          {node.content_blocks.map((block, index) => (
            <div
              key={`${block.kind}-${index}`}
              className="lax-workflow-stage-container__teaser"
            >
              {block.preview}
            </div>
          ))}
        </div>
      ) : null}

      {expanded && effectivelyRunning && runningLoopTree ? (
        <div
          className={
            isItem
              ? 'lax-workflow-parallel-item__body lax-workflow-stage-container__body'
              : 'lax-workflow-stage-container__body'
          }
          data-testid={bodyTestId(node)}
        >
          <AgentLoopView
            tree={runningLoopTree}
            showTaskListFooter={false}
            virtualized={itemLoopVirtualized}
            virtualizeThreshold={virtualizeThreshold}
            groupParallelTools={groupParallelTools}
          />
        </div>
      ) : null}

      {effectivelyDone && expanded ? (
        loopTree ? (
          <WorkflowStageDoneBody
            node={node}
            loopTree={loopTree}
            testId={bodyTestId(node)}
            virtualized={itemLoopVirtualized}
            virtualizeThreshold={virtualizeThreshold}
            groupParallelTools={groupParallelTools}
            bodyClassName={
              isItem
                ? 'lax-workflow-parallel-item__body lax-workflow-stage-container__body'
                : 'lax-workflow-stage-container__body'
            }
          />
        ) : (
          <div
            className="lax-workflow-stage-container__body lax-workflow-loop-replay-pending"
            data-testid={bodyTestId(node)}
          >
            {teaserText ? <p className="lax-workflow-stage-container__teaser">{teaserText}</p> : null}
            <p className="lax-workflow-loop-replay-pending__hint">
              {replayHandler ? 'Loading loop details…' : 'Loop details evicted from memory.'}
            </p>
          </div>
        )
      ) : null}
    </div>
  );
}

function workflowStageContainerPropsEqual(
  prev: WorkflowStageContainerProps,
  next: WorkflowStageContainerProps,
): boolean {
  if (prev.suppressHostScopedLoop !== next.suppressHostScopedLoop) return false;
  if (prev.subtreeHasActiveWork !== next.subtreeHasActiveWork) return false;
  if (prev.loopTree !== next.loopTree) return false;
  if (!workflowContainerViewPropsEqual(prev, next)) return false;
  return workflowContainerNodeVisualEqual(prev.node, next.node);
}

export const WorkflowStageContainer = memo(WorkflowStageContainerInner, workflowStageContainerPropsEqual);
